import { brand } from '../config';

const ASSOCIATION_ERROR_MESSAGES_MAPPING = {
  ACCOUNT_NOT_FOUND: (type) =>
    `We cannot find an account associated with this ${type}. Please try again using your phone number or contact Customer Service at 1-800-600-5402 for support.`,
  ACCOUNT_NOT_FOUND_PHONE: () =>
    'We cannot find an account associated with this phone number. Please try a new phone number or contact Customer Service at 1-800-600-5402 for support.',
  ACCOUNT_DIFFERENT: (type) => `This ${type} is associated with a different account. Please contact Customer Service at 1-800-600-5402 for support.`,
  EMAIL_MISMATCH: () => 'The email you entered does not match our records. Please contact Customer Service at 1-800-600-5402 for support.',
  INVALID_PASSCODE: () => 'You have entered an invalid passcode. Please try again.',
};

/**
 * Error codes mapping service error codes to user-friendly error messages.
 * Format: "serviceErrorCode:errorCode": "ErrorMessage".
 *
 * @constant ASSOCIATION_ERROR_CODES
 * @description A mapping of error codes to error mapping constants.
 * @type {Object.<string, string>}
 */

const ASSOCIATION_ERROR_CODES = {
  // LID
  '7025:45017': 'ACCOUNT_NOT_FOUND',
  '0:45216': 'ACCOUNT_NOT_FOUND',

  // CC
  '45006:45006': 'ACCOUNT_NOT_FOUND',
  '0:45230': 'ACCOUNT_NOT_FOUND',

  // PHONE
  '7140:45041': 'ACCOUNT_NOT_FOUND_PHONE',
  '7118:45027': 'ACCOUNT_NOT_FOUND_PHONE',

  // LID / CC
  '7041:45086': 'ACCOUNT_NOT_FOUND',

  // LID / CC / PHONE
  '7108:45006': 'ACCOUNT_DIFFERENT',
  '7129:45082': 'EMAIL_MISMATCH',
  '7123:45077': 'INVALID_PASSCODE',
};

const TYPES = {
  LID: 'Loyallist ID',
  CC: "Bloomingdale's credit card",
  PHONE: 'phone number',
};

/**
 * Retrieves an error message based on the provided type and error details.
 *
 * @param {string} type - The loyallist flow type.
 * @param {object} errorDetails - Details about the error, including serviceErrorCode, errorCode, and message.
 * @param {string} defaultErrorMessage - A default error message to use if no specific message is found.
 * @returns {string} - The error message.
 */

export const getAssociationErrorMessage = (type = '', errorDetails = {}, defaultErrorMessage = '') => {
  const { serviceErrorCode = 0, errorCode = 0, message = '' } = errorDetails;
  const key = `${serviceErrorCode}:${errorCode}`;
  const messageType = ASSOCIATION_ERROR_CODES[key];

  if (messageType && ASSOCIATION_ERROR_MESSAGES_MAPPING[messageType]) {
    return ASSOCIATION_ERROR_MESSAGES_MAPPING[messageType](TYPES[type]);
  }

  return message || defaultErrorMessage(TYPES[type]);
};

/**
 * A mapping of service error codes to corresponding error codes, keyed by brand.
 * Format: { "brand": { "serviceErrorCode": "errorCode", ... }, ... }.
 *
 * @constant {Object.<string, Object.<string, string>>} UNIFIED_SUMMARY_ERROR_CODES
 * @description Defines the mapping between service error codes and error codes, specific to each brand.
 */

const UNIFIED_SUMMARY_ERROR_CODES = {
  mcom: {
    7112: '45012',
  },
  bcom: {},
};

/**
 * Retrieves the error message from the provided error details if the serviceErrorCode and errorCode
 * match predefined values in UNIFIED_SUMMARY_ERROR_CODES for the current brand.
 * Returns the default error message otherwise.
 *
 * @param {Object} errorDetails - An object containing details about the error,
 * including serviceErrorCode, errorCode, and message.
 * @param {string} defaultErrorMessage - The default error message to return if no matching error codes are found.
 * @returns {string} - The error message if a match is found; otherwise, the default error message.
 */

export const getUnifiedSummaryErrorMessage = (errorDetails = {}, defaultErrorMessage = '') => {
  const { serviceErrorCode = 0, errorCode = 0, message = '' } = errorDetails;
  if (UNIFIED_SUMMARY_ERROR_CODES[brand][`${serviceErrorCode}`] === `${errorCode}` && message) {
    return message;
  }
  return defaultErrorMessage;
};

export default {
  getAssociationErrorMessage,
  getUnifiedSummaryErrorMessage,
};
